import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { getUserSettings } from '../../services/user';
import { CategoriesEnum } from '../../common/enum';

/**
 * Manages column visibility based on the selected category and the user's saved preferences.
 *
 * @param optionalColumns  An array of optional columns .
 * @param selectedCategory The currently active category.
 * @param userSettingKey   The key in the user's settings.
 *
 * @returns {
 *   filteredColumns,      Columns relevant to the current category.
 *   hiddenColumns,        The actual column objects marked as hidden by the user.
 *   displayedColumns      Columns that remain visible after filtering out hidden ones.
 * }
 */
export function useColumnPreferences(
  optionalColumns: any[],
  selectedCategory: CategoriesEnum | null,
  userSettingKey: string
) {
  // Filter columns by the user’s chosen category
  const filteredColumns = useMemo(() => {
    if (!selectedCategory) return optionalColumns;
    return optionalColumns.filter((col) => col.visibleFor.includes(selectedCategory));
  }, [optionalColumns, selectedCategory]);

  // Fetch user settings for hidden columns from DB (JSON)
  const { data: hiddenMapping } = useQuery(
    ['userSettings', userSettingKey],
    () => getUserSettings(userSettingKey),
    {
      initialData: { settingsJson: '{}' },
      select: (data) => {
        try {
          return data?.settingsJson ? JSON.parse(data.settingsJson) : {};
        } catch {
          return {};
        }
      },
    }
  );

  // Identify which column fields are hidden for the current category
  const hiddenFields = useMemo(() => {
    if (!selectedCategory) return [];
    return hiddenMapping?.[selectedCategory] || [];
  }, [hiddenMapping, selectedCategory]);

  // Build an array of the actual column objects that match those hidden fields
  const hiddenColumns = useMemo(() => {
    return filteredColumns.filter((col) => hiddenFields.includes(col.field));
  }, [filteredColumns, hiddenFields]);

  // Exclude hidden columns from the filtered set to produce a final list of visible columns
  const displayedColumns = useMemo(() => {
    return filteredColumns.filter((col) => !hiddenFields.includes(col.field));
  }, [filteredColumns, hiddenFields]);

  return {
    filteredColumns,     
    hiddenColumns, 
    displayedColumns,    
  };
}
